import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

// Data Management Component for Admins
const DataManagementSection = () => {
  const { API } = useContext(AuthContext);
  const [dataSummary, setDataSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);

  const loadDataSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/data-summary`);
      setDataSummary(response.data.data_summary);
    } catch (error) {
      toast.error('Error al cargar resumen de datos');
    } finally {
      setLoading(false);
    }
  };

  const handleResetData = async () => {
    if (!confirmReset || confirmText !== 'LIMPIAR DATOS') {
      toast.error('Confirmación requerida');
      return;
    }

    setResetting(true);
    try {
      const response = await axios.delete(`${API}/admin/reset-test-data`);
      
      // Show detailed success message
      if (response.data && response.data.details) {
        const details = response.data.details;
        const deletedItems = [];
        if (details.users_deleted > 0) deletedItems.push(`${details.users_deleted} usuarios`);
        if (details.clases_deleted > 0) deletedItems.push(`${details.clases_deleted} clases`);
        if (details.formularios_deleted > 0) deletedItems.push(`${details.formularios_deleted} formularios`);
        if (details.preguntas_deleted > 0) deletedItems.push(`${details.preguntas_deleted} preguntas`);
        if (details.calificaciones_deleted > 0) deletedItems.push(`${details.calificaciones_deleted} calificaciones`);
        
        if (deletedItems.length > 0) {
          toast.success(`Datos eliminados: ${deletedItems.join(', ')}`);
        } else {
          toast.success('No había datos de prueba para eliminar');
        }
      } else {
        toast.success('Datos de prueba eliminados exitosamente');
      }
      
      setIsResetDialogOpen(false);
      setConfirmReset(false);
      setConfirmText('');
      
      // Reload data summary and users list immediately and with retries
      const reloadData = () => {
        loadDataSummary();
        // Reload users in the parent component by triggering a re-render
        window.dispatchEvent(new CustomEvent('usersDataChanged'));
      };
      
      reloadData();
      
      // Also reload after delays to ensure backend processing is complete
      setTimeout(reloadData, 500);
      setTimeout(reloadData, 1500);
      setTimeout(reloadData, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al limpiar datos');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card className="shadow-lg border-0 border-l-4 border-l-red-500">
      <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
        <CardTitle className="flex items-center text-red-800">
          <i className="fas fa-database mr-3 text-2xl"></i>
          Gestión de Datos del Sistema
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <p className="text-gray-600 mb-4">
            Esta sección te permite limpiar todos los datos de prueba para preparar la aplicación para uso en producción.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              variant="outline"
              onClick={loadDataSummary}
              disabled={loading}
              className="btn-hover"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Cargando...
                </>
              ) : (
                <>
                  <i className="fas fa-chart-bar mr-2"></i>
                  Ver Resumen de Datos
                </>
              )}
            </Button>

            <Button 
              variant="destructive" 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (!dataSummary) loadDataSummary();
                setIsResetDialogOpen(true);
              }}
            >
              <i className="fas fa-trash-alt mr-2"></i>
              Limpiar Datos de Prueba
            </Button>

            <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-red-800 flex items-center">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    ⚠️ Limpiar Todos los Datos de Prueba
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2">
                      🚨 ADVERTENCIA: Esta acción es IRREVERSIBLE
                    </h4>
                    <p className="text-red-700 text-sm">
                      Se eliminarán TODOS los datos de prueba incluyendo clases, usuarios, formularios, 
                      calificaciones, noticias, mensajes y archivos subidos.
                    </p>
                  </div>

                  {dataSummary && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-3">
                        📊 Datos que se eliminarán:
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>👥 Usuarios: {dataSummary.users - 1} (preserva admin)</div>
                        <div>📚 Clases: {dataSummary.clases}</div>
                        <div>📝 Formularios: {dataSummary.formularios}</div>
                        <div>❓ Preguntas: {dataSummary.preguntas}</div>
                        <div>📰 Noticias: {dataSummary.noticias}</div>
                        <div>📋 Tareas: {dataSummary.tareas}</div>
                        <div>🎯 Calificaciones: {dataSummary.calificaciones}</div>
                        <div>💬 Mensajes: {dataSummary.mensajes}</div>
                        <div>📁 Archivos: {dataSummary.uploaded_files}</div>
                      </div>
                    </div>
                  )}

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2">
                      ✅ Se preservará:
                    </h4>
                    <ul className="text-green-700 text-sm space-y-1">
                      <li>• Usuario administrador principal (admin@instituto.uy)</li>
                      <li>• Configuraciones del sistema</li>
                      <li>• Estructura de la base de datos</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="confirm-reset"
                        checked={confirmReset}
                        onChange={(e) => setConfirmReset(e.target.checked)}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="confirm-reset" className="text-sm">
                        He leído y entiendo que esta acción eliminará todos los datos de prueba
                      </Label>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm-text" className="text-sm font-medium">
                        Para confirmar, escribe: <code className="bg-gray-100 px-1 rounded">LIMPIAR DATOS</code>
                      </Label>
                      <Input
                        id="confirm-text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Escribe: LIMPIAR DATOS"
                        className="font-mono"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsResetDialogOpen(false);
                        setConfirmReset(false);
                        setConfirmText('');
                      }}
                      disabled={resetting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleResetData}
                      disabled={resetting || !confirmReset || confirmText !== 'LIMPIAR DATOS'}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {resetting ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Limpiando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-trash-alt mr-2"></i>
                          Limpiar Datos Definitivamente
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {dataSummary && (
            <div className="mt-4 p-3 md:p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2 text-sm md:text-base">📈 Estado Actual del Sistema:</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 text-xs md:text-sm">
                {Object.entries(dataSummary).map(([key, value]) => (
                  <div key={key} className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-blue-600">{value}</div>
                    <div className="text-gray-600 capitalize text-[10px] md:text-xs leading-tight">{key.replace('_', ' ')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Usuarios = () => {
  const { user, API } = useContext(AuthContext);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user.rol === 'admin') {
      loadUsuarios();
    } else {
      setLoading(false);
    }

    // Listen for custom events to reload data after deletion
    const handleDataChanged = () => {
      if (user.rol === 'admin') {
        loadUsuarios();
      }
    };

    window.addEventListener('usersDataChanged', handleDataChanged);

    return () => {
      window.removeEventListener('usersDataChanged', handleDataChanged);
    };
  }, [user.rol]);

  const loadUsuarios = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsuarios(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    nombre: '',
    apellido: '',
    email: '',
    celular: '',
    rol: '',
    activo: true
  });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const openEditDialog = (usuario) => {
    setEditingUser(usuario);
    setEditForm({
      nombre: usuario.nombre,
      apellido: usuario.apellido,
      email: usuario.email,
      celular: usuario.celular || '',
      rol: usuario.rol,
      activo: usuario.activo
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${editingUser.id}`, editForm);
      toast.success('Usuario actualizado correctamente');
      loadUsuarios();
      setIsEditDialogOpen(false);
      setEditingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const openDeleteDialog = (usuario) => {
    setUserToDelete(usuario);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success('Usuario eliminado correctamente');
      loadUsuarios();
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const getRoleColor = (rol) => {
    switch (rol) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'profesor':
        return 'bg-blue-100 text-blue-800';
      case 'pastor':
        return 'bg-purple-100 text-purple-800';
      case 'estudiante':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (rol) => {
    switch (rol) {
      case 'admin':
        return 'fas fa-crown';
      case 'profesor':
        return 'fas fa-chalkboard-teacher';
      case 'pastor':
        return 'fas fa-church';
      case 'estudiante':
        return 'fas fa-user-graduate';
      default:
        return 'fas fa-user';
    }
  };

  const filteredUsuarios = usuarios.filter(usuario => {
    const matchesFilter = filter === 'all' || usuario.rol === filter;
    const matchesSearch = 
      usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const getUserStats = () => {
    return {
      total: usuarios.length,
      admins: usuarios.filter(u => u.rol === 'admin').length,
      profesores: usuarios.filter(u => u.rol === 'profesor').length,
      pastores: usuarios.filter(u => u.rol === 'pastor').length,
      estudiantes: usuarios.filter(u => u.rol === 'estudiante').length
    };
  };

  const stats = getUserStats();

  // Only render if user is admin
  if (user.rol !== 'admin') {
    return (
      <div className="space-y-8">
        <Card className="shadow-lg border-0">
          <CardContent className="text-center py-12">
            <i className="fas fa-lock text-gray-300 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Acceso Restringido</h3>
            <p className="text-gray-600">Solo los administradores pueden acceder a esta sección.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="usuarios-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Usuarios</h1>
          <p className="text-gray-600">Administra los usuarios del sistema</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-md border-0">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-users text-gray-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{stats.total}</h3>
            <p className="text-gray-600">Total Usuarios</p>
          </CardContent>
        </Card>
        
        <Card className="shadow-md border-0">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-crown text-red-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{stats.admins}</h3>
            <p className="text-gray-600">Administradores</p>
          </CardContent>
        </Card>
        
        <Card className="shadow-md border-0">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-chalkboard-teacher text-blue-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{stats.profesores}</h3>
            <p className="text-gray-600">Profesores</p>
          </CardContent>
        </Card>
        
        <Card className="shadow-md border-0">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-user-graduate text-green-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{stats.estudiantes}</h3>
            <p className="text-gray-600">Estudiantes</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-md border-0">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1">
              <Label htmlFor="search">Buscar usuarios</Label>
              <Input
                id="search"
                placeholder="Buscar por nombre, apellido o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mt-1"
                data-testid="search-users-input"
              />
            </div>
            
            <div className="w-full lg:w-auto">
              <Label htmlFor="filter">Filtrar por rol</Label>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="mt-1" data-testid="filter-users-select">
                  <SelectValue placeholder="Filtrar por rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los roles</SelectItem>
                  <SelectItem value="admin">Administradores</SelectItem>
                  <SelectItem value="profesor">Profesores</SelectItem>
                  <SelectItem value="pastor">Pastores</SelectItem>
                  <SelectItem value="estudiante">Estudiantes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* VERSION NUEVA v3.0 - Mobile Optimized */}
      <div style={{
        backgroundColor: '#10b981',
        color: 'white',
        padding: '8px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '12px',
        marginBottom: '16px',
        borderRadius: '4px'
      }}>
        ✅ VERSIÓN MÓVIL v3.0 CARGADA
      </div>
      
      {/* Users Grid - ULTRA SIMPLIFIED FOR MOBILE */}
      {filteredUsuarios.length > 0 ? (
        <div className="space-y-4">
          {filteredUsuarios.map((usuario) => (
            <div 
              key={usuario.id} 
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                padding: '16px',
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              {/* ROW 1: Badge */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <span style={{
                  backgroundColor: usuario.rol === 'admin' ? '#fee2e2' : usuario.rol === 'profesor' ? '#dbeafe' : usuario.rol === 'pastor' ? '#f3e8ff' : '#dcfce7',
                  color: usuario.rol === 'admin' ? '#991b1b' : usuario.rol === 'profesor' ? '#1e40af' : usuario.rol === 'pastor' ? '#6b21a8' : '#166534',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  <i className={`${getRoleIcon(usuario.rol)} mr-1`}></i>
                  {usuario.rol.charAt(0).toUpperCase() + usuario.rol.slice(1)}
                </span>
                <span style={{
                  fontSize: '11px',
                  color: usuario.activo ? '#16a34a' : '#dc2626',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-circle" style={{fontSize: '8px', marginRight: '4px'}}></i>
                  {usuario.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              {/* ROW 2: Avatar + Info */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  {usuario.nombre[0]}{usuario.apellido[0]}
                </div>
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '4px',
                    lineHeight: '1.2'
                  }}>
                    {usuario.nombre} {usuario.apellido}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    lineHeight: '1.2',
                    wordBreak: 'break-all'
                  }}>
                    {usuario.email}
                  </div>
                </div>
              </div>

              {/* ROW 3: Extra Info */}
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <div style={{marginBottom: '4px'}}>
                  <i className="fas fa-calendar-plus" style={{marginRight: '6px', color: '#3b82f6'}}></i>
                  Registrado: {formatDate(usuario.fecha_registro)}
                </div>
                <div style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  <i className="fas fa-id-card" style={{marginRight: '6px', color: '#8b5cf6'}}></i>
                  ID: {usuario.id.substring(0, 16)}...
                </div>
              </div>

              {/* ROW 4: Buttons - ALWAYS STACKED */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: '100%'
              }}>
                <button
                  onClick={() => openEditDialog(usuario)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <i className="fas fa-edit"></i>
                  Editar
                </button>
                {usuario.id !== user.id && (
                  <button
                    onClick={() => openDeleteDialog(usuario)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: 'white',
                      border: '1px solid #fca5a5',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#dc2626',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <i className="fas fa-trash"></i>
                    Eliminar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="shadow-lg border-0">
          <CardContent className="text-center py-12">
            <i className="fas fa-search text-gray-300 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filter !== 'all' 
                ? 'No se encontraron usuarios' 
                : 'No hay usuarios registrados'}
            </h3>
            <p className="text-gray-600">
              {searchTerm || filter !== 'all'
                ? 'Intenta ajustar los filtros de búsqueda.'
                : 'Los usuarios aparecerán aquí cuando se registren.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Data Management Section */}
      <DataManagementSection />

      {/* User Management Info */}
      <Card className="shadow-md border-0">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <i className="fas fa-info-circle text-blue-600 mt-1"></i>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Gestión de Usuarios</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Los usuarios pueden registrarse automáticamente como estudiantes</li>
                <li>• Los profesores deben ser creados por administradores</li>
                <li>• Los administradores tienen acceso completo al sistema</li>
                <li>• Usa los filtros para encontrar usuarios específicos rápidamente</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-nombre">Nombre</Label>
                <Input
                  id="edit-nombre"
                  value={editForm.nombre}
                  onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-apellido">Apellido</Label>
                <Input
                  id="edit-apellido"
                  value={editForm.apellido}
                  onChange={(e) => setEditForm({ ...editForm, apellido: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-celular">Teléfono</Label>
              <Input
                id="edit-celular"
                value={editForm.celular}
                onChange={(e) => setEditForm({ ...editForm, celular: e.target.value })}
                placeholder="Opcional"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-fecha-nacimiento">Fecha de Nacimiento</Label>
              <Input
                id="edit-fecha-nacimiento"
                type="date"
                value={editForm.fecha_nacimiento || ''}
                onChange={(e) => setEditForm({ ...editForm, fecha_nacimiento: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="edit-rol">Rol</Label>
              <Select value={editForm.rol} onValueChange={(value) => setEditForm({ ...editForm, rol: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="estudiante">Estudiante</SelectItem>
                  <SelectItem value="pastor">Pastor</SelectItem>
                  <SelectItem value="profesor">Profesor</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-activo"
                checked={editForm.activo}
                onCheckedChange={(checked) => setEditForm({ ...editForm, activo: checked })}
              />
              <Label htmlFor="edit-activo">Usuario Activo</Label>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" className="instituto-gradient text-white">
                Guardar Cambios
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Eliminar Usuario</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              ¿Estás seguro que deseas eliminar al usuario{" "}
              <strong>{userToDelete?.nombre} {userToDelete?.apellido}</strong>?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <div className="flex items-start">
                <i className="fas fa-exclamation-triangle text-red-600 mt-1 mr-2"></i>
                <div className="text-sm text-red-800">
                  <strong>Esta acción no se puede deshacer.</strong>
                  <p className="mt-1">Se eliminarán todos los datos asociados al usuario.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
            >
              Eliminar Usuario
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Usuarios;